import { Heading, Paragraph, UtilityText } from 'component/text';
import { Modal } from 'component/modal';
import { HorizBox, Pad, PadBox } from 'component/basics';
import { Datastore, useModuleGlobal } from "util/datastore";
import { CTAButton } from 'component/button';
import { getDeploymentConfig } from "util/util";
import { ActivityIndicator, Dimensions, StyleSheet, View } from "react-native";
import { colorGarnet3, colorHoh3, colorButtonSecondary, colorTextOnLightColorTag, colorTextPrimary, colorTextSecondary, colorTextWarning, colorWhite } from 'component/color';
import { CTAButtonWithLoadingSpinner } from 'open-modules/zdf/component/button';
import { CheckmarkFilled, WarningFilled } from "@carbon/icons-react";
import { logEventAsync } from "util/eventlog";
import { defineModuleGlobal } from "util/moduledata";
import { DataReadOptions } from "util/stdtypes";

export const modulePushPermission = defineModuleGlobal<boolean>("USER_GLOBAL", "notifications", "pushPermission");
export type BrowserPushPermissionState = 'default' | 'granted' | 'denied';

/**
 * 
 * @returns The VAPID key for browser push notifications
 */
export function getPublicWebPushKey(): string | undefined {
    // TODO: Read it out from a file in case of local development?
    const config = getDeploymentConfig()
    return config.publicWebPushKey ?? undefined;
}

export function usePushNotificationPermission(options?: DataReadOptions){
    return useModuleGlobal(modulePushPermission, options)
}

export function setPushNotificationPermission({datastore, value} : {datastore: Datastore, value: boolean}): void{
    logEventAsync(datastore, "push-notifs-update-permission", { permission: value, func: "setPushNotificationPermission" });
    datastore.DEPRECATED_setModuleUserGlobal("notifications", ["pushPermission"], value)
}


type PermissionPopupProps={
    handleAllow?: () => void;
    handleNotAllow?: () => void;
    permission?: BrowserPushPermissionState,
    loading?: boolean
}

export function PermissionPopup({handleAllow, handleNotAllow, permission, loading}: PermissionPopupProps) {
    return <Modal showTopBar={false} onClose={() => {}}>
        <PermissionPopupContent handleAllow={handleAllow} handleNotAllow={handleNotAllow} permission={permission} loading={loading}/>
    </Modal>
}

export function PermissionPopupContent({handleAllow, handleNotAllow, permission="default", loading=false}: PermissionPopupProps) {
    return <View style={{ backgroundColor: colorButtonSecondary, paddingVertical: 32, paddingHorizontal: 20, justifyContent: "center", alignItems: "center", borderRadius: 20}}>
        <View>
            <Heading type="large" label={"Push Notifications"} color={colorTextPrimary}></Heading>
            <Pad size={8}></Pad>
            <Paragraph type="large" color={colorTextSecondary} label={"You will receive push notifications for replies to your posts. You can deactivate them at any time in your Account."}/>      
            <Pad size={32}></Pad>
            {
                permission === "denied" && <>
                    <HorizBox gap={8} center>
                        <UtilityText text={'⚠️'}></UtilityText>
                        <UtilityText color={colorTextWarning} label={"Your browser has denied permissions for this site. You have to enable them in the browser settings as well."} />
                    </HorizBox>
                    <Pad size={32}></Pad>
                </>
            }
            <HorizBox spread>
                <CTAButton wide type="secondary" label={"Not allow"} onPress={handleNotAllow}></CTAButton>
                <Pad />
                <CTAButtonWithLoadingSpinner wide label={"Allow"} onPress={handleAllow} processing={loading}></CTAButtonWithLoadingSpinner>
            </HorizBox>
        </View>
    </View>
}

const askForPermissionStyle =  StyleSheet.create({
    container: {
        backgroundColor: colorWhite, 
        padding: 32,
        justifyContent: "center", 
        alignItems: "center",
        width: "100%",
        height: "100%"
    },
    containerSuccess: {
        backgroundColor: colorHoh3
    },
    containerFailure: {
        backgroundColor: colorGarnet3
    }
})

export type AskForPermissionScreenProps = {
    permission?: BrowserPushPermissionState,
    loading?: boolean,
    onRequest?: ()=>void,
    already?: boolean,
    error?: boolean
}
export function AskForPermissionScreen({ permission= "default", loading = false, onRequest, already = false, error = false }: AskForPermissionScreenProps) {
    const s = askForPermissionStyle;

    const { height: screenHeight } = Dimensions.get('window');

    const getHeading = () => {
        if (permission === "denied") {
            return "Your browser blocked permissions for this domain.\nPlease unblock them in the browser settings.";
        }
        if(error){
            return "Something went wrong.\nSetting up push notifications failed."
        }
        if (permission === "granted") {
            return already
                ? "Notifications are already enabled.\nYou can close this window."
                : "Notifications are now enabled.\nYou can close this window.";
        }
        if (permission === "default") {
            return "Please confirm to allow browser notifications.";
        }
        return "";
    };

    const renderIcon = () => {
        if (permission === "denied" || error) return <WarningFilled style={iconStyle} />;
        if (permission === "granted") return <CheckmarkFilled style={iconStyle} />;
        if (loading) return <ActivityIndicator size={32} />;
        if (permission === "default") return <CTAButton size="small" label="Request permission" onPress={onRequest} />;
        return null;
    };

    const iconStyle = { height: 32, width: 32, borderRadius: 20 };

    return (
        <View style={[
            s.container,
            permission === "granted" && s.containerSuccess,
            (permission === "denied" || error) && s.containerFailure,
            {height: screenHeight}
        ]}>
            <View>
                <Heading center label={getHeading()} color={colorTextOnLightColorTag} />
            </View>
            <PadBox vert={16}>
                {renderIcon()}
            </PadBox>
        </View>
    );
}